import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import api from '../../../../services/api';

const BACKEND_SERVER = process.env.REACT_APP_BACKEND_SERVER || 'http://127.0.0.1:8000/api/';

// Biblioteca de documentos de UM cliente (Organization) - só cadastro:
// tag + data de referência, upload (múltiplo), baixar e excluir. O
// disparo pra WhatsApp/E-mail é feito numa tela separada ("Envio de
// Documentos", em lote por tag - menu Empresas), não aqui.
const OrganizationDocuments = ({ appId, organizationId }) => {
    const [folders, setFolders] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [selectedFolderId, setSelectedFolderId] = useState('all');
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState({ type: '', message: '' });

    const [uploadFolderId, setUploadFolderId] = useState('');
    const [uploadReferenceDate, setUploadReferenceDate] = useState('');
    const [uploading, setUploading] = useState(false);

    const [newFolderName, setNewFolderName] = useState('');
    const [creatingFolder, setCreatingFolder] = useState(false);

    const load = async () => {
        if (!appId || !organizationId) return;
        setLoading(true);
        try {
            const [foldersRes, documentsRes] = await Promise.all([
                api.get(`v1/apps/${appId}/document_folders/`),
                api.get(`v1/apps/${appId}/documents/?organization=${organizationId}`)
            ]);
            setFolders(foldersRes.data?.folders || []);
            setDocuments(documentsRes.data?.documents || []);
        } catch (error) {
            console.error('Erro ao carregar documentos do cliente', error);
            setStatus({ type: 'error', message: 'Erro ao carregar documentos do cliente.' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, organizationId]);

    const visibleDocuments = useMemo(
        () => (selectedFolderId === 'all' ? documents : documents.filter((d) => d.folder === selectedFolderId)),
        [documents, selectedFolderId]
    );

    const folderById = useMemo(() => Object.fromEntries(folders.map((f) => [f.id, f])), [folders]);

    const handleCreateFolder = async () => {
        const name = newFolderName.trim();
        if (!name) return;
        setCreatingFolder(true);
        try {
            const response = await api.post(`v1/apps/${appId}/document_folders/`, { name });
            setFolders((prev) => [...prev, response.data]);
            setNewFolderName('');
        } catch (error) {
            console.error('Erro ao criar tag', error);
            setStatus({ type: 'error', message: 'Erro ao criar tag.' });
        } finally {
            setCreatingFolder(false);
        }
    };

    const handleUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        if (files.length === 0) return;

        setUploading(true);
        try {
            for (const file of files) {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('organization_id', organizationId);
                if (uploadFolderId) formData.append('folder_id', uploadFolderId);
                if (uploadReferenceDate) formData.append('reference_date', uploadReferenceDate);
                await api.post(`v1/apps/${appId}/documents/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            }
            await load();
        } catch (error) {
            console.error('Erro ao enviar documento', error);
            setStatus({ type: 'error', message: 'Erro ao enviar documento.' });
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteDocument = async (doc) => {
        if (!window.confirm(`Excluir "${doc.name}"? Essa ação não pode ser desfeita.`)) return;
        try {
            await api.delete(`v1/apps/${appId}/documents/${doc.id}/`);
            setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
        } catch (error) {
            console.error('Erro ao excluir documento', error);
            setStatus({ type: 'error', message: 'Erro ao excluir documento.' });
        }
    };

    const getDocumentUrl = (doc) => `${BACKEND_SERVER}v1/apps/${appId}/media/${doc.r2_key}`;

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <Box sx={{ color: '#fff' }}>
            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            {/* Tags */}
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#ccc' }}>
                Tags
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Chip
                    label="Todos"
                    onClick={() => setSelectedFolderId('all')}
                    sx={{ fontWeight: 600, bgcolor: selectedFolderId === 'all' ? '#38bdf8' : 'rgba(255,255,255,0.08)', color: selectedFolderId === 'all' ? '#000' : '#fff' }}
                />
                {folders.map((folder) => (
                    <Chip
                        key={folder.id}
                        label={folder.name}
                        onClick={() => setSelectedFolderId(folder.id)}
                        sx={{
                            fontWeight: 600,
                            bgcolor: selectedFolderId === folder.id ? folder.color || '#38bdf8' : 'rgba(255,255,255,0.08)',
                            color: selectedFolderId === folder.id ? '#000' : '#fff'
                        }}
                    />
                ))}
                <TextField
                    size="small"
                    placeholder="Nova tag..."
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                    sx={{ width: 140, '& input': { color: '#fff' } }}
                />
                <IconButton size="small" onClick={handleCreateFolder} disabled={creatingFolder || !newFolderName.trim()} sx={{ color: '#38bdf8' }}>
                    <AddIcon fontSize="small" />
                </IconButton>
            </Stack>

            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />

            {/* Upload */}
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#ccc' }}>
                Adicionar documento(s)
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems="center">
                <TextField
                    select
                    size="small"
                    label="Tag"
                    value={uploadFolderId}
                    onChange={(e) => setUploadFolderId(e.target.value)}
                    sx={{ minWidth: 160, '& .MuiInputLabel-root': { color: '#ccc' }, '& .MuiOutlinedInput-root': { color: '#fff' } }}
                >
                    <MenuItem value="">Sem tag</MenuItem>
                    {folders.map((f) => (
                        <MenuItem key={f.id} value={f.id}>
                            {f.name}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    size="small"
                    type="date"
                    label="Data de referência"
                    InputLabelProps={{ shrink: true }}
                    value={uploadReferenceDate}
                    onChange={(e) => setUploadReferenceDate(e.target.value)}
                    sx={{ minWidth: 180, '& .MuiInputLabel-root': { color: '#ccc' }, '& input': { color: '#fff' } }}
                />
                <Button variant="outlined" component="label" disabled={uploading} sx={{ color: '#38bdf8', borderColor: '#38bdf8' }}>
                    {uploading ? 'Enviando...' : 'Escolher arquivo(s)'}
                    <input type="file" hidden multiple onChange={handleUpload} />
                </Button>
            </Stack>

            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />

            {/* Lista de documentos */}
            {visibleDocuments.length === 0 ? (
                <Typography variant="body2" sx={{ opacity: 0.6 }}>
                    Nenhum documento nessa tag ainda.
                </Typography>
            ) : (
                <List>
                    {visibleDocuments.map((doc) => (
                        <ListItem
                            key={doc.id}
                            sx={{ borderRadius: 2, mb: 1, bgcolor: 'rgba(255,255,255,0.05)' }}
                            secondaryAction={
                                <Stack direction="row" spacing={0.5}>
                                    <Tooltip title="Baixar">
                                        <IconButton size="small" component="a" href={getDocumentUrl(doc)} target="_blank" rel="noreferrer" sx={{ color: '#aaa' }}>
                                            <DownloadIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Excluir">
                                        <IconButton size="small" onClick={() => handleDeleteDocument(doc)} sx={{ color: '#ef4444' }}>
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            }
                        >
                            <ListItemText
                                primary={doc.name}
                                secondary={
                                    <>
                                        {doc.folder && folderById[doc.folder] && (
                                            <Chip label={folderById[doc.folder].name} size="small" sx={{ mr: 1, height: 20, bgcolor: folderById[doc.folder].color || '#555', color: '#fff' }} />
                                        )}
                                        {doc.reference_date ? `Referência: ${doc.reference_date}` : ''}
                                    </>
                                }
                                primaryTypographyProps={{ sx: { color: '#fff', fontWeight: 600 } }}
                                secondaryTypographyProps={{ component: 'div', sx: { color: '#aaa', mt: 0.5 } }}
                            />
                        </ListItem>
                    ))}
                </List>
            )}
        </Box>
    );
};

export default OrganizationDocuments;
